
import { useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export function useTextToSpeech(isAuthenticated: boolean) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  if (typeof window !== 'undefined' && !audioElementRef.current) {
    audioElementRef.current = new Audio();
    audioElementRef.current.onended = () => {
      setIsSpeaking(false);
    };
  }

  // Function to convert text to speech using ElevenLabs
  const speakText = async (text: string, voiceId?: string | null): Promise<void> => {
    if (!text.trim() || isSpeaking) return;

    try {
      // Check authentication first
      if (!isAuthenticated) {
        toast({
          title: 'Authentication Required',
          description: 'Please sign in to use voice features.',
          variant: 'destructive'
        });
        return;
      }
      
      setIsSpeaking(true);

      // Call our Supabase Edge Function for text-to-speech
      const { data, error } = await supabase.functions.invoke('elevenlabs', {
        body: {
          action: 'tts',
          text: text,
          voiceId: voiceId // Pass the agent's voice ID if available
        }
      });

      if (error) {
        console.error('Error in text to speech:', error);
        toast({
          title: 'Text-to-Speech Error',
          description: 'Failed to generate speech. Please try again.',
          variant: 'destructive'
        });
        setIsSpeaking(false);
        throw error;
      }

      if (!data.success) {
        setIsSpeaking(false);
        throw new Error(data.error || 'Unknown error');
      }

      // Convert base64 to blob and play audio
      const binaryAudio = atob(data.audioContent);
      const audioBytes = new Uint8Array(binaryAudio.length);
      for (let i = 0; i < binaryAudio.length; i++) {
        audioBytes[i] = binaryAudio.charCodeAt(i);
      }

      const audioBlob = new Blob([audioBytes], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioElementRef.current) {
        audioElementRef.current.src = audioUrl;
        audioElementRef.current.play().catch(err => {
          console.error('Error playing audio:', err);
          setIsSpeaking(false);
        });
      }
    } catch (error) {
      console.error('Error in text to speech:', error);
      setIsSpeaking(false);
    }
  };

  // Function to stop speaking
  const stopSpeaking = () => {
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      setIsSpeaking(false);
    }
  };

  return {
    isSpeaking,
    speakText,
    stopSpeaking
  };
}
